import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { BRANDING } from "@/config/branding";

const Brand = () => (
  <div className="flex items-center gap-3">
    <img src={BRANDING.logo} alt="ALM" className="h-12 w-12 rounded-2xl object-contain bg-white/90 p-1 shadow-sm" />
    <div className="leading-tight">
      <div className="font-head font-extrabold text-2xl text-foreground">{BRANDING.productName}</div>
      <div className="text-xs font-semibold tracking-wide text-muted-foreground">{BRANDING.productSignature}</div>
    </div>
  </div>
);

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, email: "", reason: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, reason: "not_found" }); return; }
    api.get(`/auth/invite/${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, valid: r.data.valid, email: r.data.email || "", reason: r.data.reason || "" }))
      .catch(() => setState({ loading: false, valid: false, reason: "error" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("La password deve avere almeno 8 caratteri.");
    if (password !== confirm) return setErr("Le password non coincidono.");
    setSubmitting(true);
    try {
      await api.post("/auth/invite/accept", { token, password });
      window.location.href = "/";
    } catch (e2) {
      setErr(apiErr(e2, "Impossibile completare la registrazione."));
      setSubmitting(false);
    }
  };

  const reasonMsg = {
    not_found: "Invito non valido.",
    used: "Questo invito è già stato utilizzato.",
    revoked: "Questo invito è stato revocato.",
    expired: "Questo invito è scaduto.",
    error: "Impossibile verificare l'invito.",
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6 py-12">
      <div className="w-full max-w-sm space-y-6" data-testid="accept-invite-card">
        <Brand />
        {state.loading && <p className="text-sm text-muted-foreground" data-testid="invite-loading">Verifica invito…</p>}

        {!state.loading && !state.valid && (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4" data-testid="invite-invalid">
            <h1 className="font-head text-xl font-bold text-destructive">Invito non disponibile</h1>
            <p className="text-sm text-muted-foreground mt-1">{reasonMsg[state.reason] || "Invito non valido."}</p>
            <a href="/login" className="inline-block mt-4 text-sm font-semibold text-primary underline">Vai al login</a>
          </div>
        )}

        {!state.loading && state.valid && (
          <form onSubmit={submit} className="space-y-5" data-testid="accept-invite-form">
            <div>
              <h1 className="font-head text-2xl font-bold">Crea il tuo account</h1>
              <p className="text-sm text-muted-foreground">Imposta una password per <span className="font-semibold">{state.email}</span></p>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input value={state.email} disabled className="h-12" data-testid="invite-email" />
            </div>
            <div className="space-y-1">
              <Label>Password (min. 8 caratteri)</Label>
              <div className="relative">
                <Input data-testid="invite-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pr-11" autoComplete="new-password" />
                <button type="button" data-testid="invite-password-toggle" onClick={() => setShowPw((v) => !v)}
                  title={showPw ? "Nascondi password" : "Mostra password"} aria-label={showPw ? "Nascondi password" : "Mostra password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Conferma password</Label>
              <div className="relative">
                <Input data-testid="invite-password-confirm" type={showConfirm ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 pr-11" autoComplete="new-password" />
                <button type="button" data-testid="invite-password-confirm-toggle" onClick={() => setShowConfirm((v) => !v)}
                  title={showConfirm ? "Nascondi password" : "Mostra password"} aria-label={showConfirm ? "Nascondi password" : "Mostra password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            {err && <p data-testid="invite-error" className="text-sm text-destructive font-medium">{err}</p>}
            <Button data-testid="invite-submit" type="submit" disabled={submitting} className="w-full h-12 rounded-full text-base font-semibold">
              {submitting ? "Creazione…" : "Crea account ed entra"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
